import type { EventDateRange } from '../../shared/types';

export function isoDay(date: Date): string { return date.toISOString().slice(0, 10); }
export function parseDay(iso: string): Date { return new Date(`${iso}T12:00:00Z`); }
export function shiftDay(iso: string, days: number): string {
  const date = parseDay(iso); date.setUTCDate(date.getUTCDate() + days); return isoDay(date);
}
export function selectedRange(first: string, second: string): EventDateRange {
  const [start, end] = [first, second].sort();
  return start === end ? { start } : { start, end };
}
export function monthDays(month: string): string[][] {
  const first = parseDay(`${month}-01`);
  const start = shiftDay(isoDay(first), -first.getUTCDay());
  return Array.from({ length: 6 }, (_, week) => Array.from({ length: 7 }, (_, day) => shiftDay(start, week * 7 + day)));
}
export function shiftMonth(month: string, count: number): string {
  const date = parseDay(`${month}-01`); date.setUTCMonth(date.getUTCMonth() + count); return isoDay(date).slice(0, 7);
}

const MONTH_NAMES = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
/**
 * A typed date as YYYY-MM-DD, or null when it can't be read as one. Takes 3/15/2010 (with slashes, dashes or dots),
 * 2010-03-15, and March 15, 2010 or 15 March 2010. A two-digit year is the latest one not after `today` ('past', as
 * for a birthday), the one in this century ('ahead', as for an event), or the one nearest `today` ('nearest').
 */
export function parseTypedDate(text: string, today: string, years: TwoDigitYears = 'past'): string | null {
  const parts = dateParts(normalize(text));
  return parts && parts.y !== undefined ? isoOf(parts.y, parts.m, parts.d, today, years) : null;
}
export type TwoDigitYears = 'past' | 'ahead' | 'nearest';

/**
 * A typed day or range of days, or null when it can't be read. A range is two dates with a dash, "to" or "through"
 * between them, such as 6/12/2026 – 6/13/2026. The first may leave out its year, or its month and year when the second
 * names its month (6/12 – 6/13/2026, June 12–13, 2026). A range typed backwards is turned around.
 */
export function parseTypedRange(text: string, today: string): EventDateRange | null {
  const s = normalize(text);
  const day = parseTypedDate(s, today, 'ahead');
  if (day) return { start: day };
  // A dash also separates the parts of one date, so each is tried until both sides read as dates.
  for (const separator of s.matchAll(/ ?(?:[-–—]|\bto\b|\bthrough\b|\bthru\b) ?/g)) {
    const before = s.slice(0, separator.index), after = s.slice(separator.index! + separator[0].length);
    const end = parseTypedDate(after, today, 'ahead') ?? (() => {
      // June 12–13, 2026: the second is a day and year in the first's month.
      const first = dateParts(before), match = after.match(/^(\d{1,2})(?:st|nd|rd|th)? (\d{2}|\d{4})$/);
      return first && first.y === undefined && match ? isoOf(+match[2], first.m, +match[1], today, 'ahead') : null;
    })();
    if (!end) continue;
    const first = dateParts(before);
    if (!first) continue;
    const endYear = Number(end.slice(0, 4));
    let start = first.y === undefined ? isoOf(endYear, first.m, first.d, today, 'ahead') : isoOf(first.y, first.m, first.d, today, 'ahead');
    // Dec 30 – Jan 2, 2027 runs over the new year.
    if (start && first.y === undefined && start > end) start = isoOf(endYear - 1, first.m, first.d, today, 'ahead');
    if (start) return selectedRange(start, end);
  }
  return null;
}

function normalize(text: string): string { return text.trim().toLowerCase().replace(/,/g, ' ').replace(/\s+/g, ' '); }

/** A date's parts, with the year left out when it isn't typed (3/15, March 15). */
function dateParts(s: string): { y?: number; m: number; d: number } | null {
  let match: RegExpMatchArray | null;
  if ((match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) return { y: +match[1], m: +match[2], d: +match[3] };
  if ((match = s.match(/^(\d{1,2}) ?[/.-] ?(\d{1,2})(?: ?[/.-] ?(\d{2}|\d{4}))?$/))) return { m: +match[1], d: +match[2], y: match[3] ? +match[3] : undefined };
  if ((match = s.match(/^([a-z]+)\.? (\d{1,2})(?:st|nd|rd|th)?(?: (\d{2}|\d{4}))?$/))) return { m: monthNumber(match[1]), d: +match[2], y: match[3] ? +match[3] : undefined };
  if ((match = s.match(/^(\d{1,2}) ([a-z]+)\.?(?: (\d{2}|\d{4}))?$/))) return { d: +match[1], m: monthNumber(match[2]), y: match[3] ? +match[3] : undefined };
  return null;
}

function isoOf(year: number, m: number, d: number, today: string, years: TwoDigitYears): string | null {
  let y = year;
  if (y < 100) {
    const now = Number(today.slice(0, 4)), century = Math.floor(now / 100) * 100;
    if (years === 'ahead') y += century;
    else if (years === 'past') y += century + y <= now ? century : century - 100;
    else y = [century - 100, century, century + 100].map(c => c + y).sort((a, b) => Math.abs(a - now) - Math.abs(b - now))[0];
  }
  if (!m || m > 12 || !d) return null;
  const iso = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return isoDay(parseDay(iso)) === iso ? iso : null;
}

/** A month's number from its name or an abbreviation of three letters or more, or 0. */
function monthNumber(name: string): number {
  return name.length >= 3 ? MONTH_NAMES.findIndex(month => month.startsWith(name)) + 1 : 0;
}
